import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Share2 } from "lucide-react";
import { copyText } from "@/components/room/RoomHeader";
import { StoryLine } from "@/components/room/StoryThread";
import { AgeBadge, ThemeBadge } from "@/components/yarn/Badges";
import { CatFace } from "@/components/yarn/Cat";
import { PageHeader } from "@/components/yarn/PageHeader";
import { YarnBall } from "@/components/yarn/YarnBall";
import { useAuth } from "@/context/AuthContext";
import { api, errorText } from "@/lib/api";
import { niceDate } from "@/pages/StoriesPage";

function Cast({ players }) {
  return (
    <div className="flex flex-wrap gap-3" data-testid="story-cast">
      {players.map((p) => (
        <div key={p.id} className="flex items-center gap-2 rounded-full bg-white border-2 border-[var(--yarn-stitch)] pl-1 pr-3 py-1">
          <CatFace id={p.id} look={p.cat} size={30} />
          <span className="font-display text-sm">{p.username}</span>
          <span className="text-xs text-[var(--yarn-muted)]">{p.lines} woven</span>
        </div>
      ))}
    </div>
  );
}

export default function StoryPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user === null) return;
    api.get(`/stories/${id}`).then((r) => setStory(r.data)).catch((e) => setError(errorText(e)));
  }, [id, user]);

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: `A Yarn story: ${story.room_name}`, text: story.lines[0]?.text, url }).catch(() => {});
    else copyText(url, "Story link copied");
  };
  const login = () => {
    sessionStorage.setItem("yarn_next", `/story/${id}`);
    navigate("/");
  };

  const header = <PageHeader title="A Yarn story" back={user ? "/stories" : "/"} right={story && (
    <button className="btn-yarn h-10 px-4 text-sm inline-flex items-center gap-2" onClick={share} data-testid="share-story-button">
      <Share2 size={15} /> Share
    </button>
  )} />;

  if (error) {
    return (
      <div className="flex-1">
        {header}
        <div className="max-w-md mx-auto px-5 py-16">
          <div className="patch patch-stitched p-8 text-center" data-testid="story-error">
            <YarnBall size={48} color="#991B1B" className="mx-auto" />
            <p className="font-display text-xl mt-4">{error}</p>
            {!user && <button className="btn-yarn h-12 px-6 mt-5" onClick={login} data-testid="story-login-button">Log in</button>}
          </div>
        </div>
      </div>
    );
  }

  if (!story) return <div className="flex-1">{header}<div className="py-24 grid place-items-center"><YarnBall size={48} className="yarn-spin" /></div></div>;

  return (
    <div className="flex-1" data-testid="story-page">
      {header}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-6">
        <div className="rise-in">
          <div className="flex items-center gap-2 flex-wrap">
            <ThemeBadge theme={story.theme} />
            <AgeBadge rating={story.age_rating} />
            <span className="text-xs text-[var(--yarn-muted)]">{niceDate(story.created_at)} · {story.lines.length} lines</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3" data-testid="story-title">{story.room_name}</h1>
        </div>
        <Cast players={story.players} />
        <article className="patch p-5 sm:p-8" data-testid="story-article">
          <div className="relative pl-10 pt-1">
            <span className="thread-line" />
            {story.lines.map((line, i) => <StoryLine key={i} line={line} index={i} />)}
          </div>
          {story.ended_reason && <p className="text-sm text-[var(--yarn-muted)] mt-2 text-center" data-testid="story-ended-reason">The end. ({story.ended_reason})</p>}
        </article>
        {!user && (
          <div className="patch p-6 text-center">
            <p className="font-display text-lg">Want to spin your own?</p>
            <button className="btn-yarn h-12 px-6 mt-3" onClick={() => navigate("/")} data-testid="play-yarn-button">Play Yarn</button>
          </div>
        )}
      </main>
    </div>
  );
}
